'use client';

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@nextui-org/react';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import React, { useState } from 'react';

import {
  BestValuePricing,
  BgSubcription,
  CreditYellowBuyIcon,
  IconCheckPricing,
  IconPromaxPricing,
  IconProPricing,
  IconRocketInPricing,
  IconStartedPricing,
  ImageBgBuyCredit,
  MostPopularPricing,
  OnlyOncePricing,
} from '@/assets';
import { Buttons } from '@/components';
import ClientOnly from '@/components/client-only';
import { MIXPANEL_TRACK } from '@/configs/constant';
import LoginModal from '@/layouts/header/Login';
import { useAuth } from '@/provider/UserProvider';
import { postCancel, registerSubscription, stopCancelSubscription } from '@/services/pricing';

import { SubscriptionResponse, TabKey } from '.';
import { getMarketplaceURL } from '@/layouts/sidebar/helpers';
import Link from 'next/link';
import Image from 'next/image';

const Subscription: React.FC<{
  data: SubscriptionResponse | null;
}> = ({ data }) => {
  const TABS = [
    {
      key: TabKey.Card,
      text: 'Master Card/VISA',
    },
    {
      key: TabKey.AIRI,
      text: 'AIRI',
      subtext: '20% OFF'
    },
  ];
  const [tabActive, setTabActive] = useState<TabKey>(TabKey.Card);
  const { auth } = useAuth();
  const isLogin = !!auth.accessToken;
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const [cancelPremiumModalOpen, setCancelPremiumModalOpen] = useState(false);

  const handlRegisterSubscription = async (subscriptionName: string) => {
    mixpanel.track(MIXPANEL_TRACK.SUBSCRIBE_NOW, {
      'Subscription Name': subscriptionName,
    });
    const res = await registerSubscription(subscriptionName);
    if (res && res.url) {
      window.location.href = res.url;
    }
  };

  const handlStopCancelSubscription = async () => {
    const res = await stopCancelSubscription();
    if (res) {
      location.reload();
    }
  };

  const handleCancelPremium = async () => {
    try {
      setCancelPremiumModalOpen(true);
      await postCancel();
      location.reload();
    } catch (error) {
      console.error('Error canceling premium:', error);
    }
  };

  const listOptsPricing = [
    {
      typeRegister: 'starter',
      iconOption: <IconStartedPricing />,
      name: 'Starter',
      listAccess: [
        {
          title: '1200 credits',
          icon: <IconCheckPricing />,
          tooltip: <IconRocketInPricing />,
        },
        {
          title: 'Unlimited 2x upscaling',
          icon: <IconCheckPricing />,
        },
        { title: 'Maximum batch per run 2 images', icon: <IconCheckPricing /> },
      ],
      iconTagOption: <OnlyOncePricing />,
      discountPrice: '$0.99',
      btnNotUse: 'Buy now',
      btnNoSub: 'Buy now',
      // btnOnUse: 'Cancel this plan',
      btnSwap: 'Switch plan',
    },
    {
      typeRegister: 'unlimitedPro',
      iconOption: <IconProPricing />,
      name: 'Unlimited Pro',
      listAccess: [
        {
          title: '600 credits',
          icon: <IconCheckPricing />,
          tooltip: null,
        },
        {
          title: 'Everything from Starter',
          icon: <IconCheckPricing />,
        },
        { title: 'Unlimited 4x upscaling', icon: <IconCheckPricing /> },
        { title: 'Maximum batch per run 4 image', icon: <IconCheckPricing /> },
      ],
      iconTagOption: <MostPopularPricing />,
      discountPrice: '$5.95',
      btnNotUse: 'Subscribe now',
      btnNoSub: 'Subscribe now',
      btnOnUse: 'Cancel this plan',
      btnSwap: 'Switch plan',
    },
    {
      typeRegister: 'unlimitedProMax',
      iconOption: <IconPromaxPricing />,
      name: 'Unlimited Pro Max',
      listAccess: [
        {
          title: '1000 Credits',
          icon: <IconCheckPricing />,
          tooltip: null,
        },
        { title: 'Everything from Unlimited Pro', icon: <IconCheckPricing /> },
      ],
      iconTagOption: <BestValuePricing />,
      discountPrice: '$7.99',
      btnNotUse: 'Subscribe now',
      btnNoSub: 'Subscribe now',
      btnOnUse: 'Cancel this plan',
      btnSwap: 'Switch plan',
    },
  ];

  return (
    <div className=" pb-25 md:max-w-2xl">
      <div className="main-container relative">
        <div className=" justify-center pb-10 text-center  xl:pb-[20px]">
          <div>
            {auth?.user.isPlus ? (
              <span className="font-sora text-[40px] font-semibold text-black md:text-[32px]">
                All aiRight plans
              </span>
            ) : (
              <span className="font-sora text-[40px] font-semibold text-black md:text-[32px]">
                Subscribe to aiRight Plus plan
              </span>
            )}
          </div>
          {auth?.user.isPlus ? (
            <div className="pt-[16px]">
              <span className="text-[16px] font-normal leading-6 text-gray-600">
                You’re currently a
              </span>
              <span className="pl-[4px] text-[16px] font-semibold leading-6 text-orange-500">
                Plus member
              </span>
            </div>
          ) : (
            <span className="text-[16px] font-normal leading-6 text-gray-600">
              Become a Plus member and get access to all we have to offer
            </span>
          )}
        </div>
        <div className="flex justify-center mb-10">
          <div className="flex p-[6px] bg-[#F8F9FD] rounded-full w-[480px] max-w-full">
            {TABS.map(tab => (
              <div
                aria-checked={tab.key === tabActive}
                onClick={() => setTabActive(tab.key)}
                className="text-center cursor-pointer aria-checked:bg-white p-[10px] rounded-full text-[14px] font-semibold w-[50%]" 
                key={tab.key}
              >
                {tab.text} {tab.subtext && <span className="text-orange-500 bg-orange-50 rounded-full py-1 px-[10px] ml-1">{tab.subtext}</span>}
              </div>
            ))}
          </div>
        </div>
        <div aria-checked={tabActive === TabKey.Card} className="hidden aria-checked:block">
          {isLogin && data?.subscription && auth.user.isPlus && (
          <div className="hidden w-full max-w-[320px] gap-4 rounded-2xl border-1 border-gray-100 bg-white p-5 shadow-md xl:static xl:mx-auto xl:mb-5 xl:mt-2 xl:block md:mb-0">
            <p className="text-16 font-semibold leading-[25px]">
              {listOptsPricing
                .filter((item) => item.typeRegister === data.subscription.plan)
                .map((item) => item.name) || data.subscription.plan.toUpperCase()}
            </p>
            <div>
              <div className="flex items-center justify-between pt-2 xl:flex-col xl:items-start">
                {data.subscription.plan !== 'starter' && data.subscription.status === 'active' ? (
                  <span>Next billing date:</span>
                ) : (
                  <span>End date:</span>
                )}
                <span className="font-bold">
                  {data?.subscription &&
                    moment(data.subscription.enddate * 1000).format('DD MMM, YYYY')}
                </span>
              </div>
            </div>
            {data.subscription.plan !== 'starter' ? (
              data.subscription.status === 'active' ? (
                <Buttons
                  className="w-full px-[20px] py-[12.5px] text-[15px] font-semibold leading-6 "
                  onClick={() => {
                    setCancelPremiumModalOpen(true);
                  }}
                  typeBtn="secondary"
                >
                  Cancel my plan
                </Buttons>
              ) : (
                <Buttons
                  className="block w-full py-[5px] "
                  onClick={() => {
                    handlStopCancelSubscription();
                  }}
                  typeBtn="secondary"
                >
                  <div className="text-15 font-semibold leading-4 ">Un cancel</div>
                  {/* <div className="text-12 font-normal leading-4 ">
                    Valid until{' '}
                    <span className="font-bold">
                      {moment(data.subscription.enddate * 1000).format('DD MMM, YYYY')}
                    </span>
                  </div> */}
                </Buttons>
              )
            ) : (
              <></>
            )}
          </div>
        )}

        <div className="relative flex flex-wrap justify-center justify-items-center gap-[20px] md:gap-0">
          {listOptsPricing.map((optionPlan) => (
            <div
              className={`relative flex w-[calc(33%-10px)] max-w-[380px] flex-col justify-between rounded-[11px] border-1 border-gray-200 bg-white px-5 py-8 xl:w-[calc(50%-10px)] xl:pt-10 md:mt-[20px] md:w-full ${
                data?.subscription
                  ? data?.subscription.plan === optionPlan.typeRegister &&
                    'border-2 border-orange-500'
                  : optionPlan.typeRegister === 'starter' && 'border-2 border-orange-500'
              } `}
            >
              <div>
                <div className="flex items-center gap-[10px]">
                  {optionPlan.iconOption}
                  <div className="text-[16px] font-semibold leading-5">{optionPlan.name}</div>
                </div>
                <div className="absolute -right-[6px] top-2 scale-75 xl:-right-2 xl:top-1">
                  {optionPlan.iconTagOption}
                </div>

                <div className="flex items-center pt-3 2xl:block">
                  <div>
                    <span className="text-[40px] font-bold md:text-[32px]">
                      {optionPlan.discountPrice}
                    </span>
                    <span className="pl-[4px] text-[16px] font-normal leading-5 text-[#121212BD]">
                      /week
                    </span>
                  </div>
                </div>
                <div className="grid gap-y-[16px] pb-[8%] pt-3 text-[14px] leading-6">
                  {optionPlan.listAccess.map((item) => (
                    <>
                      <div className="flex items-center">
                        <p className="pr-[10px]">{item.icon}</p>
                        <p
                          className={`flex gap-2 ${
                            item.tooltip
                              ? 'bg-gradient-to-r from-[#DA613A] via-[#EB476B] to-[#7D3DDC] bg-clip-text text-[16px] font-semibold text-transparent'
                              : ''
                          }`}
                        >
                          {item.title}
                          {item.tooltip}
                        </p>
                        {/* {item?.subTitle && <p className="pr-[10px]">{item.subTitle}</p>} */}

                        {/* {item?.tooltip && <span className="flex items-center">{item.tooltip}</span>} */}
                      </div>
                    </>
                  ))}
                </div>
              </div>
              <ClientOnly>
                {isLogin ? (
                  data?.subscription ? (
                    data.subscription.plan === optionPlan.typeRegister ? (
                      data.subscription.status === 'active' ? (
                        // optionPlan.btnOnUse ? (
                        //   <Buttons
                        //     className="w-full bg-black px-[20px] py-[12.5px] text-[15px] font-semibold leading-6 text-white"
                        //     onPress={() => {
                        //       setCancelPremiumModalOpen(true);
                        //     }}
                        //   >
                        //     {optionPlan.btnOnUse}
                        //   </Buttons>
                        // ) : (
                        //   <></>
                        // )
                        <></>
                      ) : (
                        // <Buttons
                        //   className="block w-full bg-black py-[5px] "
                        //   onClick={() => {
                        //     handlStopCancelSubscription();
                        //   }}
                        // >
                        //   <div className="text-15 font-semibold leading-4 text-[#FFFFFF]">
                        //     Subscribe again
                        //   </div>
                        //   <div className="text-12 font-normal leading-4 text-gray-200">
                        //     Valid until{' '}
                        //     <span className="font-bold">
                        //       {moment(data.subscription.enddate * 1000).format('DD MMM, YYYY')}
                        //     </span>
                        //   </div>
                        // </Buttons>
                        <></>
                      )
                    ) : optionPlan?.btnSwap ? (
                      <Buttons
                        className="w-full px-[20px] py-[12.5px] text-[15px] font-semibold leading-7 text-[#FFFFFF]"
                        onClick={() => {
                          optionPlan.typeRegister === 'starter'
                            ? data.planOnlyOnce.starter
                              ? ''
                              : handlRegisterSubscription(optionPlan.typeRegister)
                            : handlRegisterSubscription(optionPlan.typeRegister);
                        }}
                        typeBtn={
                          optionPlan.typeRegister === 'starter'
                            ? data.planOnlyOnce.starter
                              ? 'default'
                              : 'primary'
                            : 'primary'
                        }
                      >
                        {optionPlan.typeRegister === 'starter'
                          ? data.planOnlyOnce.starter
                            ? 'Already purchased'
                            : optionPlan.btnSwap
                          : optionPlan.btnSwap}
                      </Buttons>
                    ) : (
                      <></>
                    )
                  ) : (
                    <Buttons
                      className="w-full px-[20px] py-[12.5px] text-[15px] font-semibold leading-6 text-[#FFFFFF]"
                      onClick={() => {
                        optionPlan.typeRegister === 'starter'
                          ? !data?.planOnlyOnce.starter
                            ? handlRegisterSubscription(optionPlan.typeRegister)
                            : ''
                          : handlRegisterSubscription(optionPlan.typeRegister);
                      }}
                      typeBtn={
                        optionPlan.typeRegister === 'starter'
                          ? !data?.planOnlyOnce.starter
                            ? 'primary'
                            : 'default'
                          : 'primary'
                      }
                    >
                      {optionPlan.typeRegister === 'starter'
                        ? !data?.planOnlyOnce.starter
                          ? optionPlan.btnNoSub
                          : 'Already purchased'
                        : optionPlan.btnNoSub}
                    </Buttons>
                  )
                ) : (
                  <Buttons
                    className="w-full bg-[url('/images/bg-button-pricing-plus.png')] px-[20px] py-[12.5px] text-[15px] font-semibold leading-6 text-[#FFFFFF]"
                    onClick={onOpen}
                  >
                    {optionPlan.btnNotUse}
                  </Buttons>
                )}
              </ClientOnly>
            </div>
          ))}
        </div>
        <div className="flex justify-center justify-items-center gap-[20px] xl:hidden">
          <div className="w-[calc(33%-10px)] max-w-[380px] " />
          <div className="w-[calc(33%-10px)] max-w-[380px] " />
          {isLogin && data?.subscription && auth.user.isPlus && (
            <div className="float-right -mr-[60px] mt-[40px] grid w-full max-w-[320px] gap-4 rounded-2xl border-1 border-gray-100 bg-white p-5 shadow-md xl:static xl:mx-auto xl:mb-5 xl:mt-2 md:mb-0 md:max-w-none">
              <p className="text-16 font-semibold leading-[25px]">
                {listOptsPricing
                  .filter((item) => item.typeRegister === data.subscription.plan)
                  .map((item) => item.name) || data.subscription.plan.toUpperCase()}
              </p>
              <div>
                <div className="flex items-center justify-between pt-2 xl:flex-col xl:items-start">
                  {data.subscription.plan !== 'starter' && data.subscription.status === 'active' ? (
                    <span>Next billing date:</span>
                  ) : (
                    <span>End date:</span>
                  )}
                  <span className="font-bold">
                    {data?.subscription &&
                      moment(data.subscription.enddate * 1000).format('DD MMM, YYYY')}
                  </span>
                </div>
              </div>
              {data.subscription.plan !== 'starter' ? (
                data.subscription.status === 'active' ? (
                  <Buttons
                    className="w-full px-[20px] py-[12.5px] text-[15px] font-semibold leading-6 "
                    onClick={() => {
                      setCancelPremiumModalOpen(true);
                    }}
                    typeBtn="secondary"
                  >
                    Cancel my plan
                  </Buttons>
                ) : (
                  <Buttons
                    className="block w-full py-[5px] "
                    onClick={() => {
                      handlStopCancelSubscription();
                    }}
                    typeBtn="secondary"
                  >
                    <div className="text-15 font-semibold leading-4 ">Un cancel</div>
                    {/* <div className="text-12 font-normal leading-4 ">
                    Valid until{' '}
                    <span className="font-bold">
                      {moment(data.subscription.enddate * 1000).format('DD MMM, YYYY')}
                    </span>
                  </div> */}
                  </Buttons>
                )
              ) : (
                <></>
              )}
            </div>
          )}
        </div>
        </div>
        <div aria-checked={tabActive === TabKey.AIRI} className="hidden aria-checked:block">
          <div className="flex justify-center">
            <div
              style={{ background: 'linear-gradient(85deg, #FFE5DD 2.51%, #FFBAA3 106.89%)' }}
              className="rounded-[20px] px-5 py-4 relative overflow-hidden"
            >
              <Image className="absolute left-0 top-0 w-full h-full" alt="bg" src={ImageBgBuyCredit} />
              <div
                className="flex gap-[12px]"
              >
                <div className="w-[42px] h-[42px] relative rounded-full bg-[#FEF2D3] flex justify-center items-center">
                  <CreditYellowBuyIcon className="scale-75" />
                </div>
                <div className="text-left w-[calc(100%-54px)] relative">
                  <p className="text-[15px] font-semibold">Buy Credits with AIRI</p>
                  <p className="text-[14px]">Buy credits to unlock access to aiRight features.</p>
                </div>
              </div>
              <Link
                target="_blank"
                href={getMarketplaceURL(auth.isOwallet || false, '/buy-credit')}
                className="w-full hover:opacity-80 mt-5 mb-3 text-center bg-orange-500 relative block text-white font-medium text-[14px] rounded-full py-2 px-3"
              >
                Buy now
              </Link>
            </div>
          </div>
        </div>
      </div>
      <LoginModal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} />
      {data && (
        <Modal
          isOpen={cancelPremiumModalOpen}
          onOpenChange={(open) => setCancelPremiumModalOpen(open)}
          placement="center"
          hideCloseButton
          classNames={{ base: 'p-[20px] rounded-[24px]' }}
        >
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className="flex flex-col gap-1 text-[24px]">
                  Cancel{' '}
                  {listOptsPricing
                    .filter((item) => item.typeRegister === data.subscription.plan)
                    .map((item) => item.name) || data.subscription.plan.toUpperCase()}
                  plan?
                </ModalHeader>
                <ModalBody>
                  {data?.subscription && (
                    <p className="text-[16px] text-gray-500">
                      Are you sure you want to cancel this plan? You will be able to use the service
                      until the next bill on{' '}
                      <span className="font-bold">
                        {moment(data.subscription.enddate * 1000).format('DD MMM, YYYY')}
                      </span>
                    </p>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    className="rounded-[100px] bg-red-600 text-white"
                    onPress={onClose}
                    onClick={handleCancelPremium}
                  >
                    Cancel plan
                  </Button>
                  <Button className=" rounded-[100px] bg-gray-50" onPress={onClose}>
                    Close
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      )}
      <Image className="absolute w-full left-0 right-0 bottom-0 z-[-1]" alt="bg" src={BgSubcription} />
    </div>
  );
};

export default Subscription;
